// material-ui
import { Box, Drawer } from '@mui/material';

// project imports
import useConfig from 'hooks/useConfig';
import { appDrawerWidth } from 'store/constant';
import PogozoTealTheme from 'views/calendar/PogozoTealTheme';
import TasksPanel from 'views/calendar/TasksPanel';

interface TaskPanelDockProps {
    overlay: boolean;
}

// ==============================|| TASK PANEL DOCK ||============================== //

/**
 * Cross-route singleton: the right-hand Tasks dock (POGO-13786). Mounted in
 * MainLayout's flex row, outside <Outlet/>, so it survives route changes just
 * like the Header / SubNav / Snackbar. It reuses the Calendar panel verbatim,
 * restoring the teal accent via the same PogozoTealTheme wrap the Calendar page
 * applies, and is sized by the dock (the panel still takes no props).
 *
 * At md+ (`overlay === false`) it renders as a sticky inline column that pushes
 * <main> narrower. Below md (`overlay === true`) it renders as a right-anchored
 * temporary Drawer (backdrop + onClose) — mirroring how the left Sidebar
 * degrades to variant="temporary" below md — so narrow content is not crushed.
 * Dismissing the overlay unpins the panel, since the header pin is the only
 * control and the preference should reflect what is on screen.
 */
const TaskPanelDock = ({ overlay }: TaskPanelDockProps) => {
    const { onChangeTaskPanelPinned } = useConfig();

    const body = (
        <PogozoTealTheme>
            <TasksPanel />
        </PogozoTealTheme>
    );

    if (overlay) {
        return (
            <Drawer
                anchor="right"
                open
                variant="temporary"
                onClose={() => onChangeTaskPanelPinned(false)}
                ModalProps={{ keepMounted: true }}
                PaperProps={{ sx: { width: appDrawerWidth, mt: '88px', px: 2 } }}
            >
                {body}
            </Drawer>
        );
    }

    return <Box sx={{ width: appDrawerWidth, flexShrink: 0, mt: '88px', position: 'sticky', top: '88px', px: 2 }}>{body}</Box>;
};

export default TaskPanelDock;
